import type { PhaseLabel } from '../../helpers/phase-labels/phase-labels.type';
import {
  BurnsDataOverTime,
  FlowByPaceDataOverTime,
  FlowByPhaseDataOverTime,
  HistoricalBurnsDataOverTime,
  PaceStatus,
  WorkPeriod,
} from '../../api/work-periods-client/work-periods-client.type';

enum Metric {
  Tasks = 'tasks',
  Points = 'points',
}

// Stable, non-translated key for the work-pace chart type. Use this for
// state, branching, and helper signatures so logic survives language
// switches; render the user-visible label via t('work_periods.burn_up')
// or t('work_periods.burn_down') at the call site.
enum WorkPaceChartType {
  BurnDown = 'burn_down',
  BurnUp = 'burn_up',
}

enum WorkPeriodType {
  Defined = 'defined',
  Custom = 'custom',
}

enum WorkPeriodTabType {
  SINGLE = 'single',
  COMPARISON = 'comparison',
}

enum WorkPeriodsTab {
  ASSESSMENT = 'assessment',
  WORK_PHASE_DISTRIBUTION = 'work_phase_distribution',
  WORK_PACE = 'work_pace',
  DELIVERY_CONFIDENCE = 'delivery_confidence',
  BREAKDOWN = 'breakdown',
  COMPARISON_TABLE = 'comparison_table',
  COMPARISON_MEASURE_COMPARISON = 'comparison_measure_comparison',
  COMPARISON_MEASURES_OVER_TIME = 'comparison_measures_over_time',
}

enum WorkPeriodTense {
  Future = 'future',
  Past = 'past',
  Present = 'present',
}

type MeasureMetadata = {
  measure_name: string;
  measure_title: string;
  measure_group: string;
  measure_description: string;
  measure_units: string;
  is_zero_valid: boolean;
  is_custom?: boolean;
  desired_trendline_direction?: 'up' | 'down';
};

type MeasureData = {
  value: number;
  change: number;
  target: number | null;
};

type KeyProcessMeasuresData = {
  task_completion: MeasureData;
  strategy: MeasureData;
  complexity: MeasureData;
  quality: MeasureData;
  scope_creep: MeasureData;
  readiness: MeasureData;
  independence: MeasureData;
};

type ReadinessData = {
  readiness: Record<string, number>;
};

type PhaseChartDataPoint = {
  date: string;
  [phaseId: string]: string | number;
};

type PaceChartDataPoint = {
  key: string;
  date: string;
  actual: number;
  ideal: number;
  remaining: number;
  complete: number;
  total: number;
  status: PaceStatus;
  statusColor: string;
};

type DeliveryDataPoint = {
  date: string;
  key: string;
  completed: number;
  scope: number;
  scopeChange: number[];
};

type HistoricalBurnsDataPoint = {
  date: string;
  key: string;
  average: number;
  band: number[];
};

type DeliveryConfidenceChartDataPoint = {
  date: string;
  key: string;
  completed: number;
  scope: number;
  scopeChange: number[];
  // we don't always have the blue band data
  average?: number;
  band?: number[];
};

type FlowData = {
  flow_by_phase: FlowByPhaseDataOverTime;
  flow_by_pace: FlowByPaceDataOverTime;
  burns: BurnsDataOverTime;
  historical_burns: HistoricalBurnsDataOverTime;
  /**
   * Per-workflow display labels for the seven canonical phase keys, emitted
   * by the backend on phase-aware measure responses (`flow_by_phase`,
   * `burns_and_flows`, `bloomcast_by_phase`, `task_flow`). Absent on
   * non-phase-aware responses; consumers should route through
   * `resolvePhaseLabels` for a defensive merge with i18n defaults.
   */
  phases?: PhaseLabel[];
};

type HealthScoreData = {
  health_scores: {
    score_by_points: string;
  };
};

type TaskRecord = {
  id: string;
  name: string;
  title: string;
  description: string;
  points: number;
  type: string;
  url: string;
  created_date: string;
  transitions?: {
    status: string;
    changed_by: string;
    changed_at: string;
  }[];
  epic?: {
    id: string;
    name: string;
    title: string;
    url: string;
  } | null;
};

/**
 * Date range for ad-hoc work periods
 */
export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

type WorkPeriodsState = {
  selectedWorkPeriodType: WorkPeriodType;
  selectedWorkPeriods: WorkPeriod[];
  dateRange: DateRange;
  isLoading: boolean;
  scoreData: HealthScoreData | undefined;
  keyMeasuresData: KeyProcessMeasuresData | undefined;
  flowData: FlowData | undefined;
  customMeasures?: MeasureMetadata[];
  measureData?: Record<string, any>;
  isFetching?: boolean;
  selectedBoardId: string | null;
  teamId: string | null;
  formattedBoards: { value: string; label: string }[];
  tasksByPhase?: Record<string, TaskRecord[]>;
  tasksByMeasure?: Record<string, TaskRecord[]>;
  tasksByDate?: Record<string, TaskRecord[]>;
  workPeriods: WorkPeriod[];
};

type WorkPeriodsContextType = WorkPeriodsState & {
  /**
   * Set the team ID
   * @param teamId The team ID
   */
  setTeamId: (teamId: string | null) => void;

  /**
   * Set the selected board ID
   * @param boardId The board ID
   */
  setSelectedBoardId: (boardId: string | null) => void;

  /**
   * Set the selected work period type (Defined or Custom)
   * @param type The work period type
   */
  setSelectedWorkPeriodType: (type: WorkPeriodType) => void;

  /**
   * Set the selected work periods
   * @param workPeriods The work periods to select
   */
  setSelectedWorkPeriods: (workPeriods: WorkPeriod[]) => void;

  /**
   * Set the date range for ad-hoc work periods
   * @param dateRange The date range
   */
  setDateRange: (dateRange: DateRange) => void;

  /**
   * Fetch work periods for a board
   * @param boardId The board ID
   */
  fetchWorkPeriods: (boardId?: string | null) => Promise<void>;

  /**
   * Fetch data for the selected work periods
   */
  fetchWorkPeriodData: () => Promise<void>;

  /**
   * Handle board selection
   * @param boardId The board ID
   */
  handleBoardSelect: (boardId: string | null) => void;

  /**
   * Handle work period type selection
   * @param type The work period type
   */
  handleWorkPeriodTypeSelect: (type: WorkPeriodType) => void;

  /**
   * Handle date range selection for ad-hoc work periods
   * @param range The date range
   */
  handleDateRangeChange: (range: DateRange) => void;

  /**
   * Handle ad-hoc work period submission
   */
  handleAdHocSubmit: () => void;

  /**
   * Handle work period selection
   * @param workPeriod The work period to select
   * @param isSelected Whether the work period is selected
   */
  handleWorkPeriodSelect: (workPeriod: WorkPeriod, isSelected: boolean) => void;
};

export { Metric, WorkPaceChartType, WorkPeriodsTab, WorkPeriodTabType, WorkPeriodTense, WorkPeriodType };

export type {
  DeliveryConfidenceChartDataPoint,
  DeliveryDataPoint,
  FlowData,
  HealthScoreData,
  HistoricalBurnsDataPoint,
  KeyProcessMeasuresData,
  MeasureMetadata,
  PaceChartDataPoint,
  PhaseChartDataPoint,
  ReadinessData,
  WorkPeriod,
  WorkPeriodsContextType,
};
